import Code from '@/components/dashboard/Code';
import SectionActions from '@/docs/SectionActions';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';
import { useRef } from 'react'

export default function JavaScriptSDKDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="JavaScript/TypeScript SDK" description="The AxionQuant JavaScript SDK is a TypeScript-first wrapper for the Financial Data API; full type safety, async/await support, and helpers for every market data endpoint." canonicalUrl="https://axionquant.com/docs/client-libraries/javascript-SDK" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">AxionQuant JavaScript/TypeScript SDK</DocHeading>
            <p className="text-lg text-gray-700 max-w-[900px]">
              The AxionQuant JavaScript SDK (also called the Axion SDK) provides a comprehensive TypeScript-first wrapper for interacting with the AxionQuant Financial API.
              This SDK offers full type safety, async/await support, and automatic error handling for financial data including ESG scores,
              stock prices, cryptocurrency data, forex, futures, indices, ETFs, economic indicators, company financials, insider transactions,
              SEC filings, earnings transcripts, news, sentiment analysis, supply chain, web traffic, and credit ratings.
            </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* ---------- Installation ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Installation</h2>
            <p className="text-gray-700">
              Install the SDK via npm, yarn, or pnpm. The package includes both CommonJS and ES module builds,
              with full TypeScript definitions.
            </p>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="npm / yarn / pnpm" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">npm / yarn / pnpm</p>
            <Code lang="bash">{`# npm
npm install @axionquant/sdk

# yarn
yarn add @axionquant/sdk

# pnpm
pnpm add @axionquant/sdk`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Quick Start ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Quick Start</h2>
            <p className="text-gray-700">
              Initialize the client and start fetching data. All methods return Promises and are fully typed.
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4 mb-3">
            <p className="font-mono text-sm text-gray-800 mb-1">Steps</p>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
              <li>Import the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Axion</code> class</li>
              <li>Create a client with your API key</li>
              <li>Call methods on category properties (e.g., <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">client.esg.data()</code>)</li>
              <li>Use <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">async/await</code> to handle responses</li>
            </ol>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Quick Start (TypeScript)" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Quick Start (TypeScript)</p>
            <Code lang="typescript">{`import { Axion } from '@axionquant/sdk';

const client = new Axion('your_api_key_here');

// Get ESG data for Apple
const esgData = await client.esg.data('AAPL');
console.log(esgData);

// Get stock prices for Microsoft
const prices = await client.stocks.prices('MSFT', {
  from: '2024-01-01',
  to: '2024-03-01'
});
console.log(prices);

// Get economic indicators
const gdp = await client.econ.dataset('GDP_USA');

// Get company news
const news = await client.news.company('AAPL');

// Get ETF holdings
const holdings = await client.etfs.holdings('SPY');`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Client Initialization ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Client Initialization</h2>
            <p className="text-gray-700 mb-6">
              The <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">Axion</code> client can be initialized with or without an API key.
              When no key is provided at construction, it must be included in the <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">Authorization</code> header of each request.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Constructor Parameters</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">apiKey</td>
                    <td className="px-4 py-3 text-sm text-gray-700">string</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        Optional
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      Your AxionQuant API key. If omitted, you must provide it per request.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="TypeScript Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">TypeScript Example</p>
            <Code lang="typescript">{`import { Axion } from '@axionquant/sdk';
import type { ApiResponse } from '@axionquant/sdk';

const client = new Axion('your_api_key_here');

async function fetchFinancialData() {
  try {
    const esgData: ApiResponse = await client.esg.data('AAPL');
    const stockPrices: ApiResponse = await client.stocks.prices('MSFT');
    return { esgData, stockPrices };
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- API Categories Overview ---------- */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">API Categories</h2>
          <p className="text-gray-700 max-w-[900px] mb-6">
            The SDK is organized into 18 specialized API classes, each accessible as a property of the main client.
            All methods are promise-based and fully typed.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1400px]">
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">client.credit</h4>
            <p className="text-sm text-blue-800 mb-2">Credit ratings & entity search</p>
            <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">credit.search(query)</code>
            <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">credit.ratings(entityId)</code>
          </div>
          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-900 mb-2">client.esg</h4>
            <p className="text-sm text-green-800 mb-2">Environmental, Social, Governance</p>
            <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">esg.data(ticker)</code>
          </div>
          <div className="bg-purple-50 p-5 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-900 mb-2">client.etfs</h4>
            <p className="text-sm text-purple-800 mb-2">ETF fund data, holdings, exposure</p>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.tickers(params?)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.ticker(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.prices(ticker, params?)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.quote(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.fund(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.holdings(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.holdingsAll(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.weights(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.exposure(ticker)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.gainers(params?)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.losers(params?)</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.list(column)</code>
          </div>
          <div className="bg-yellow-50 p-5 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-900 mb-2">client.supplyChain</h4>
            <p className="text-sm text-yellow-800 mb-2">Customers, peers, suppliers</p>
            <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">supplyChain.customers(ticker)</code>
            <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">supplyChain.peers(ticker)</code>
            <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">supplyChain.suppliers(ticker)</code>
          </div>
          <div className="bg-red-50 p-5 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-900 mb-2">client.stocks</h4>
            <p className="text-sm text-red-800 mb-2">Stock quotes, prices, tickers</p>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.tickers(params?)</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.ticker(ticker)</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.prices(ticker, params?)</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.quote(ticker)</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.gainers(params?)</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.losers(params?)</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.list(column)</code>
          </div>
          <div className="bg-indigo-50 p-5 rounded-lg border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-2">client.crypto</h4>
            <p className="text-sm text-indigo-800 mb-2">Cryptocurrency data</p>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.tickers(params?)</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.ticker(ticker)</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.prices(ticker, params?)</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.quote(ticker)</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.gainers(params?)</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.losers(params?)</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.list(column)</code>
          </div>
          <div className="bg-pink-50 p-5 rounded-lg border border-pink-200">
            <h4 className="font-semibold text-pink-900 mb-2">client.profiles</h4>
            <p className="text-sm text-pink-800 mb-2">Company profiles, info, statistics</p>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.profile(ticker)</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.info(ticker)</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.summary(ticker)</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.statistics(ticker)</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.recommendation(ticker)</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.calendar(ticker)</code>
          </div>
          <div className="bg-teal-50 p-5 rounded-lg border border-teal-200">
            <h4 className="font-semibold text-teal-900 mb-2">client.sentiment</h4>
            <p className="text-sm text-teal-800 mb-2">News & social sentiment</p>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">sentiment.social(ticker)</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">sentiment.news(ticker)</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">sentiment.analyst(ticker)</code>
          </div>
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">client.financials</h4>
            <p className="text-sm text-gray-800 mb-2">Comprehensive financial metrics</p>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.revenue(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.balanceSheet(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.incomeStatement(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.cashFlowStatement(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.metrics(ticker)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.snapshot(ticker)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.dcfValue(ticker)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.dcfRate(ticker)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.eps(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.pe(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.marketCap(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.roe(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.enterpriseValue(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.ebitda(ticker, params?)</code>
            <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.debtToEquity(ticker, params?)</code>
          </div>
          <div className="bg-orange-50 p-5 rounded-lg border border-orange-200">
            <h4 className="font-semibold text-orange-900 mb-2">client.earnings</h4>
            <p className="text-sm text-orange-800 mb-2">Earnings history, trends, reports</p>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.history(ticker)</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.trend(ticker)</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.index(ticker)</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.report(ticker, params)</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.transcript(ticker, params)</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.transcriptSentiment(id)</code>
          </div>
          <div className="bg-cyan-50 p-5 rounded-lg border border-cyan-200">
            <h4 className="font-semibold text-cyan-900 mb-2">client.filings</h4>
            <p className="text-sm text-cyan-800 mb-2">SEC filings and forms</p>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.recent(ticker, params?)</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.history(ticker, formType, params?)</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.listForms()</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.search(params)</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.documentText(documentId)</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.documentSentiment(documentId)</code>
          </div>
          <div className="bg-emerald-50 p-5 rounded-lg border border-emerald-200">
            <h4 className="font-semibold text-emerald-900 mb-2">client.insiders</h4>
            <p className="text-sm text-emerald-800 mb-2">Insider transactions & ownership</p>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.funds(ticker)</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.individuals(ticker)</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.institutions(ticker)</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.ownership(ticker)</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.activity(ticker)</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.transactions(ticker)</code>
          </div>
          <div className="bg-slate-50 p-5 rounded-lg border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-2">client.forex</h4>
            <p className="text-sm text-slate-800 mb-2">Foreign exchange currency data</p>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.tickers(params?)</code>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.ticker(ticker)</code>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.prices(ticker, params?)</code>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.quote(ticker)</code>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.gainers(params?)</code>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.losers(params?)</code>
            <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.list(column)</code>
          </div>
          <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-900 mb-2">client.futures</h4>
            <p className="text-sm text-amber-800 mb-2">Commodity & financial futures</p>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.tickers(params?)</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.ticker(ticker)</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.prices(ticker, params?)</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.quote(ticker)</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.gainers(params?)</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.losers(params?)</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.list(column)</code>
          </div>
          <div className="bg-violet-50 p-5 rounded-lg border border-violet-200">
            <h4 className="font-semibold text-violet-900 mb-2">client.indices</h4>
            <p className="text-sm text-violet-800 mb-2">Stock market indices</p>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.tickers(params?)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.ticker(ticker)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.prices(ticker, params?)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.quote(ticker)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.components(ticker)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.exposure(ticker)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.gainers(params?)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.losers(params?)</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.list(column)</code>
          </div>
          <div className="bg-rose-50 p-5 rounded-lg border border-rose-200">
            <h4 className="font-semibold text-rose-900 mb-2">client.econ</h4>
            <p className="text-sm text-rose-800 mb-2">Economic indicators & calendar</p>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.find(query)</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.search(query)</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.dataset(seriesId)</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.calendar(params?)</code>
          </div>
          <div className="bg-lime-50 p-5 rounded-lg border border-lime-200">
            <h4 className="font-semibold text-lime-900 mb-2">client.news</h4>
            <p className="text-sm text-lime-800 mb-2">Financial news by company/country</p>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.general()</code>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.company(ticker)</code>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.country(country)</code>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.category(category)</code>
          </div>
          <div className="bg-sky-50 p-5 rounded-lg border border-sky-200">
            <h4 className="font-semibold text-sky-900 mb-2">client.webTraffic</h4>
            <p className="text-sm text-sky-800 mb-2">Website traffic analytics</p>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">webTraffic.traffic(ticker)</code>
          </div>
        </div>
      </div>

      {/* ---------- Profiles API (Enhanced) ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Profiles API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.profiles
            </code>
            <p className="text-gray-700 mb-6">
              Comprehensive company profiles, statistics, recommendations, and calendar events.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">profile(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get asset profile</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">recommendation(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get recommendation trends</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">statistics(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get key statistics</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">summary(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get summary details</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">calendar(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get earnings & dividend calendar</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">info(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get company info (summary profile)</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Profiles API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Profiles API - Example</p>
            <Code lang="typescript">{`// Company profile
const profile = await client.profiles.profile('AAPL');
console.log(\`Company: \${profile.name}\`);

// Recommendation trends
const recommendations = await client.profiles.recommendation('AAPL');
console.log(\`Strong Buy: \${recommendations.strongBuy}\`);

// Key statistics
const stats = await client.profiles.statistics('AAPL');
console.log(\`Market Cap: \${stats.marketCap}\`);

// Calendar events
const calendar = await client.profiles.calendar('AAPL');
console.log(\`Next Earnings: \${calendar.earnings[0].date}\`);`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Financials API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Financials API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.financials
            </code>
            <p className="text-gray-700 mb-6">
              Detailed financial metrics, statements, and calculated ratios.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">balanceSheet(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Balance sheet statement</td>
                     <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { year?: string, quarter?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">incomeStatement(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Income statement</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { year?: string, quarter?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">cashFlowStatement(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Cash flow statement</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { year?: string, quarter?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">revenue(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Revenue data</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">netIncome(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Net income</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">totalAssets(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Total assets</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">totalLiabilities(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Total liabilities</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">stockholdersEquity(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Stockholders equity</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">currentAssets(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current assets</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">currentLiabilities(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current liabilities</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">operatingCashFlow(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Operating cash flow</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">capitalExpenditures(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">CapEx</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">freeCashFlow(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Free cash flow</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">sharesOutstandingBasic(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Basic shares</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">sharesOutstandingDiluted(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Diluted shares</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { periods?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">metrics(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Calculated metrics</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">snapshot(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Financial snapshot</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">dcfValue(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">DCF valuation</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">dcfRate(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Discount rate / WACC</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Financials API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Financials API - Example</p>
            <Code lang="typescript">{`// Revenue data for last 4 quarters
const revenue = await client.financials.revenue('AAPL', { periods: 4 });
console.log('Quarterly Revenue:', revenue);

// Key financial metrics
const metrics = await client.financials.metrics('AAPL');
console.log(\`P/E Ratio: \${metrics.pe_ratio}\`);
console.log(\`ROE: \${metrics.return_on_equity}\`);

// Complete financial snapshot
const snapshot = await client.financials.snapshot('AAPL');
console.log(snapshot);

// DCF analysis
const dcfValue = await client.financials.dcfValue('AAPL');
console.log(\`Fair Price: \${dcfValue.fairPrice}, Recommendation: \${dcfValue.recommendation}\`);

const dcfRate = await client.financials.dcfRate('AAPL');
console.log(\`WACC: \${(dcfRate.wacc * 100).toFixed(2)}%\`);

// Financial statements
const balanceSheet = await client.financials.balanceSheet('AAPL');
const income = await client.financials.incomeStatement('AAPL');
const cashflow = await client.financials.cashFlowStatement('AAPL');`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Earnings API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Earnings API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.earnings
            </code>
            <p className="text-gray-700 mb-6">
              Historical earnings data, trends, and detailed reports.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">history(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Earnings history</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">trend(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Earnings trend</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">index(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Index trend</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">report(ticker, params)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Detailed earnings report</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params: { year: string, quarter: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">transcript(ticker, params)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Earnings call transcript</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params: { year: string, quarter: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">transcriptSentiment(id)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Earnings transcript sentiment</td>
                    <td className="px-4 py-3 text-sm"><code>id: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Earnings API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Earnings API - Example</p>
            <Code lang="typescript">{`// Earnings history
const history = await client.earnings.history('MSFT');
console.log(\`EPS Q1: \${history[0].eps}\`);

// Specific quarter report
const report = await client.earnings.report('MSFT', {
  year: '2024',
  quarter: 'Q1'
});
console.log(\`Revenue: \${report.revenue}\`);
console.log(\`EPS: \${report.eps}\`);

// Earnings call transcript
const transcript = await client.earnings.transcript('MSFT', {
  year: '2024',
  quarter: 'Q1'
});
console.log(\`Transcript: \${transcript.text.substring(0, 200)}...\`);

// Transcript sentiment
const sentiment = await client.earnings.transcriptSentiment('transcript_id');
console.log(\`Sentiment: \${sentiment.score}\`);`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Filings API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Filings API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.filings
            </code>
            <p className="text-gray-700 mb-6">
              SEC filings search and retrieval.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">recent(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get recent filings</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { limit?: number, form?: string }`}</code></td>
                  </tr>
                    <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">history(ticker, formType, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get specific form type by date range</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, formType: string, params?: { startDate?: string, endDate?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">listForms()</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List available form types</td>
                    <td className="px-4 py-3 text-sm"><code>none</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">search(params)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Search filings by year/quarter</td>
                    <td className="px-4 py-3 text-sm"><code>{`params: { ticker?: string, form?: string, year: string, quarter: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">documentText(documentId)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get raw text of a filing document</td>
                    <td className="px-4 py-3 text-sm"><code>documentId: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">documentSentiment(documentId)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get sentiment analysis of a filing</td>
                    <td className="px-4 py-3 text-sm"><code>documentId: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Filings API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Filings API - Example</p>
            <Code lang="typescript">{`// Get recent 10-K filings
const filings = await client.filings.recent('AAPL', { 
  form: '10-K', 
  limit: 3 
});

// Search for Q1 2024 filings
const searchResults = await client.filings.search({
  ticker: 'AAPL',
  year: '2024',
  quarter: 'Q1'
});

// Get all 10-Q filings for Q1 2024
const forms = await client.filings.history('AAPL', '10-Q', {
  startDate: '2024-01-01',
  endDate: '2024-03-31'
});

// Get raw document text and sentiment
const text = await client.filings.documentText('document_id_here');
const sentiment = await client.filings.documentSentiment('document_id_here');
console.log(\`Sentiment: \${sentiment.score}\`);`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Insiders API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Insiders API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.insiders
            </code>
            <p className="text-gray-700 mb-6">
              Insider ownership, transactions, and institutional holdings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">funds(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Fund ownership data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">individuals(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Insider holders (individuals)</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">institutions(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Institutional ownership</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ownership(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Major holders breakdown</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">activity(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Net share purchase activity</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">transactions(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Insider transactions</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Insiders API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Insiders API - Example</p>
            <Code lang="typescript">{`// Institutional ownership
const institutions = await client.insiders.institutions('AAPL');
console.log(\`Top Holder: \${institutions[0].name} (\${institutions[0].shares} shares)\`);

// Recent insider transactions
const transactions = await client.insiders.transactions('AAPL');
transactions.slice(0,5).forEach(t => {
  console.log(\`\${t.insider}: \${t.shares} shares \${t.type}\`);
});

// Ownership breakdown
const ownership = await client.insiders.ownership('AAPL');
console.log(\`Insider Ownership: \${ownership.insider_percent}%\`);`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- WebTraffic API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Web Traffic API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.webTraffic
            </code>
            <p className="text-gray-700 mb-6">
              Website traffic data and analytics for companies.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">traffic(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get web traffic data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="WebTraffic API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">WebTraffic API - Example</p>
            <Code lang="typescript">{`// Get web traffic for Amazon
const traffic = await client.webTraffic.traffic('AMZN');
console.log(\`Monthly Visits: \${traffic.monthly_visits}\`);
console.log(\`YoY Growth: \${traffic.yoy_growth}%\`);`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- ETF API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">ETF API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.etfs
            </code>
            <p className="text-gray-700 mb-6">
              ETF tickers, quotes, prices, fund details, holdings, weights, exposure, gainers/losers, and metadata listings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Filtered list of ETF tickers</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { country?: string, exchange?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current quote for a single ETF</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { from?: string, to?: string, frame?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Real-time quote data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">fund(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Fund overview and details</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">holdings(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top holdings breakdown</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">holdingsAll(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">All holdings with weights</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">weights(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Fund allocation weights</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">exposure(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Exposure analysis</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top gaining ETFs</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top losing ETFs</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list(column)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List metadata by column</td>
                    <td className="px-4 py-3 text-sm"><code>column: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ETF API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">ETF API - Example</p>
            <Code lang="typescript">{`// All US ETFs
const usEtfs = await client.etfs.tickers({ country: 'US' });

// SPY quote
const spyQuote = await client.etfs.ticker('SPY');
console.log(\`SPY: $\${spyQuote.price}\`);

// Fund details and holdings
const spyFund = await client.etfs.fund('SPY');
const spyHoldings = await client.etfs.holdings('SPY');
const spyAllHoldings = await client.etfs.holdingsAll('SPY');

// Historical prices
const spyPrices = await client.etfs.prices('SPY', {
  from: '2024-01-01',
  to: '2024-03-31'
});`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Stocks API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Stocks API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.stocks
            </code>
            <p className="text-gray-700 mb-6">
              Access stock tickers, real-time quotes, historical price data, gainers/losers, and market metadata.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Filtered list of stock tickers</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { country?: string, exchange?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current quote for a single symbol</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { from?: string, to?: string, frame?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Real-time quote data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top gaining stocks</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number, market?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top losing stocks</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number, market?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list(column)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List metadata by column</td>
                    <td className="px-4 py-3 text-sm"><code>column: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Stocks API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Stocks API - Example</p>
            <Code lang="typescript">{`// All US tickers
const usStocks = await client.stocks.tickers({ country: 'US' });

// Apple quote
const appleQuote = await client.stocks.ticker('AAPL');
console.log(\`Price: \${appleQuote.price}\`);

// Historical weekly prices for Microsoft
const msftPrices = await client.stocks.prices('MSFT', {
  from: '2024-01-01',
  to: '2024-03-31',
  frame: 'weekly'
});

// Top gainers this week
const gainers = await client.stocks.gainers({ days: 7, limit: 10 });

// List available sectors
const sectors = await client.stocks.list('sector');`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Crypto API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cryptocurrency API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.crypto
            </code>
            <p className="text-gray-700 mb-6">
              Cryptocurrency tickers, real-time quotes, historical price data, gainers/losers, and metadata listings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all crypto tickers</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { type?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get quote by symbol</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { from?: string, to?: string, frame?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Real-time quote data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top gaining cryptocurrencies</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top losing cryptocurrencies</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list(column)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List metadata by column</td>
                    <td className="px-4 py-3 text-sm"><code>column: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Crypto API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Crypto API - Example</p>
            <Code lang="typescript">{`// All cryptocurrencies
const allCrypto = await client.crypto.tickers();

// Bitcoin quote
const btc = await client.crypto.ticker('BTC-USD');
console.log(\`Bitcoin: $\${btc.price}\`);

// Ethereum historical daily prices
const ethPrices = await client.crypto.prices('ETH-USD', {
  from: '2024-01-01',
  to: '2024-03-31',
  frame: 'daily'
});

// Top crypto gainers
const topGainers = await client.crypto.gainers({ days: 1, limit: 5 });`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Economic API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Economic API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.econ
            </code>
            <p className="text-gray-700 mb-6">
              Economic indicators, FRED search, datasets, and calendar events.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">find(query)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">FRED series search</td>
                    <td className="px-4 py-3 text-sm"><code>query: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">search(query)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Search for economic series</td>
                    <td className="px-4 py-3 text-sm"><code>query: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">dataset(seriesId)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get series observations</td>
                    <td className="px-4 py-3 text-sm"><code>seriesId: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">calendar(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Economic calendar with filters</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { from?: string, to?: string, country?: string, minImportance?: number, currency?: string, category?: string, limit?: number }`}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Economic API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Economic API - Example</p>
            <Code lang="typescript">{`// FRED series search
const series = await client.econ.find('US GDP quarterly');

// Search for inflation series
const inflationSeries = await client.econ.search('inflation');

// Get GDP dataset
const gdpData = await client.econ.dataset('GDP_USA');

// Important US economic events this month
const calendar = await client.econ.calendar({
  from: '2024-04-01',
  to: '2024-04-30',
  country: 'US',
  minImportance: 3
});`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Forex API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Forex API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.forex
            </code>
            <p className="text-gray-700 mb-6">
              Foreign exchange currency tickers, quotes, historical prices, gainers/losers, and metadata listings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Filtered list of forex pairs</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { country?: string, exchange?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current quote for a currency pair</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { from?: string, to?: string, frame?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Real-time quote data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top gaining currency pairs</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top losing currency pairs</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list(column)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List metadata by column</td>
                    <td className="px-4 py-3 text-sm"><code>column: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Forex API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Forex API - Example</p>
            <Code lang="typescript">{`// All EUR forex pairs
const eurPairs = await client.forex.tickers({ country: 'EUR' });

// EUR/USD quote
const eurusd = await client.forex.ticker('EUR-USD');
console.log(\`EUR/USD: \${eurusd.price}\`);

// Historical prices
const eurGbpPrices = await client.forex.prices('EUR-GBP', {
  from: '2024-01-01',
  to: '2024-03-31',
  frame: 'daily'
});`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Futures API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Futures API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.futures
            </code>
            <p className="text-gray-700 mb-6">
              Commodity and financial futures tickers, quotes, historical prices, gainers/losers, and metadata listings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Filtered list of futures tickers</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { exchange?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current quote for a futures contract</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { from?: string, to?: string, frame?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Real-time quote data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top gaining futures contracts</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top losing futures contracts</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list(column)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List metadata by column</td>
                    <td className="px-4 py-3 text-sm"><code>column: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Futures API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Futures API - Example</p>
            <Code lang="typescript">{`// Crude oil futures quote
const crudeOil = await client.futures.ticker('CL');
console.log(\`Crude Oil: $\${crudeOil.price}\`);

// Historical gold futures prices
const goldPrices = await client.futures.prices('GC', {
  from: '2024-01-01',
  to: '2024-03-31',
  frame: 'daily'
});`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Indices API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Indices API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.indices
            </code>
            <p className="text-gray-700 mb-6">
              Stock market indices tickers, quotes, historical prices, components, exposure analysis, gainers/losers, and metadata listings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Filtered list of indices</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { exchange?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Current quote for an index</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                    <td className="px-4 py-3 text-sm"><code>{`ticker: string, params?: { from?: string, to?: string, frame?: string }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Real-time quote data</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">components(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get index constituent holdings</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">exposure(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Which indices hold a given ticker</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top gaining indices</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(params?)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Top losing indices</td>
                    <td className="px-4 py-3 text-sm"><code>{`params?: { days?: number, limit?: number }`}</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list(column)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List metadata by column</td>
                    <td className="px-4 py-3 text-sm"><code>column: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Indices API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Indices API - Example</p>
            <Code lang="typescript">{`// S&P 500 quote
const spx = await client.indices.ticker('SPX');
console.log(\`S&P 500: \${spx.price}\`);

// Dow Jones components
const dowComponents = await client.indices.components('DJI');
console.log(\`Components: \${dowComponents.length}\`);

// Which indices hold AAPL
const aaplExposure = await client.indices.exposure('AAPL');
console.log(\`Found in \${aaplExposure.indices.length} indices\`);`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- News API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">News API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.news
            </code>
            <p className="text-gray-700 mb-6">
              Financial news retrieval by company, country, and category, plus general market news.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">general()</td>
                    <td className="px-4 py-3 text-sm text-gray-700">General market news feed</td>
                    <td className="px-4 py-3 text-sm"><code>none</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">company(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">News for a specific company</td>
                    <td className="px-4 py-3 text-sm"><code>ticker: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">country(country)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">News by country</td>
                    <td className="px-4 py-3 text-sm"><code>country: string</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">category(category)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">News by category</td>
                    <td className="px-4 py-3 text-sm"><code>category: string</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="News API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">News API - Example</p>
            <Code lang="typescript">{`// General market news
const marketNews = await client.news.general();
console.log(\`Latest: \${marketNews.articles[0].headline}\`);

// News for Apple
const aaplNews = await client.news.company('AAPL');

// News by country
const ukNews = await client.news.country('GB');`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Complete Example ---------- */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Complete Example</h2>
          <p className="text-gray-700 max-w-[900px] mb-6">
            A comprehensive TypeScript application demonstrating multiple API calls, error handling, and portfolio monitoring.
          </p>
        </div>
        <Code lang="typescript">{`import { Axion } from '@axionquant/sdk';

class FinancialAnalyst {
  private client: Axion;

  constructor(apiKey: string) {
    this.client = new Axion(apiKey);
  }

  async analyzeCompany(ticker: string) {
    try {
      const [profile, esg, quote, sentiment, financials] = await Promise.all([
        this.client.profiles.profile(ticker),
        this.client.esg.data(ticker),
        this.client.stocks.ticker(ticker),
        this.client.sentiment.social(ticker),
        this.client.financials.snapshot(ticker)
      ]);

      console.log(\`\${profile.name}: $\${quote.price} | ESG: \${esg.score} | Sentiment: \${sentiment.score}\`);
      console.log(\`P/E: \${financials.pe_ratio} | Revenue: \${financials.revenue}\`);
      
      return { profile, esg, quote, sentiment, financials };
    } catch (error) {
      console.error(\`Failed to analyze \${ticker}:\`, error);
      return null;
    }
  }

  async monitorPortfolio(tickers: string[]) {
    setInterval(async () => {
      const updates = await Promise.all(
        tickers.map(t => this.client.stocks.quote(t))
      );
      console.log(new Date().toLocaleTimeString());
      updates.forEach(u => 
        console.log(\`\${u.symbol}: $\${u.price} (\${u.change_percent}%)\`)
      );
    }, 60000); // every minute
  }

  async getEconomicInsights() {
    const gdp = await this.client.econ.dataset('GDP_USA');
    const calendar = await this.client.econ.calendar({
      country: 'US',
      minImportance: 3
    });
    return { gdp, calendar };
  }

  async getETFData(ticker: string) {
    const [fund, holdings, weights] = await Promise.all([
      this.client.etfs.fund(ticker),
      this.client.etfs.holdings(ticker),
      this.client.etfs.weights(ticker)
    ]);
    return { fund, holdings, weights };
  }
}

// Usage
const analyst = new FinancialAnalyst(process.env.AXION_API_KEY!);
analyst.analyzeCompany('AAPL');
analyst.monitorPortfolio(['AAPL', 'MSFT', 'GOOGL']);
analyst.getEconomicInsights();
analyst.getETFData('SPY');`}</Code>
      </div>

      {/* ---------- Error Handling ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Error Handling</h2>
            <p className="text-gray-700 mb-6">
              The SDK throws descriptive errors that can be caught and handled gracefully.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Error Types</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Error Pattern</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr><td className="px-4 py-3 text-sm font-mono">HTTP Error {`{status}`}: {`{message}`}</td><td className="px-4 py-3 text-sm">Client or server error with status code</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">Connection Error</td><td className="px-4 py-3 text-sm">Network failure reaching the API</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">Authentication required</td><td className="px-4 py-3 text-sm">No API key provided to client</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">Request Error</td><td className="px-4 py-3 text-sm">Invalid parameters or request setup</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Error handling with retry" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Error handling with retry</p>
            <Code lang="typescript">{`try {
  const data = await client.esg.data('INVALID');
} catch (error) {
  if (error.message.includes('404')) {
    console.log('Ticker not found');
  } else if (error.message.includes('429')) {
    // Rate limit - exponential backoff
    await delay(1000);
    return fetchWithRetry();
  } else if (error.message.includes('Authentication')) {
    console.error('API key missing - provide one to the client');
  } else if (error.message.includes('Connection')) {
    console.error('Network error - check your connection');
  }
  throw error;
}`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- TypeScript Support ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">TypeScript Support</h2>
            <p className="text-gray-700 mb-6">
              The SDK is written in TypeScript and provides complete type definitions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-medium text-gray-900 mb-1">Complete Type Safety</p>
              <p className="text-xs text-gray-600">All methods have full parameter and return types.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-medium text-gray-900 mb-1">Auto-completion</p>
              <p className="text-xs text-gray-600">IDE support for all methods and parameters.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4 md:col-span-2">
              <p className="font-medium text-gray-900 mb-1">Custom Type Extensions</p>
              <p className="text-xs text-gray-600">Easily extend or override types for your domain.</p>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="TypeScript example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">TypeScript example</p>
            <Code lang="typescript">{`import { Axion } from '@axionquant/sdk';
import type { ApiResponse } from '@axionquant/sdk';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

interface CustomESG {
  score: number;
  grade: string;
  environmental: number;
}

class Analyst {
  client = new Axion(process.env.API_KEY!);

  async getESG(ticker: string): Promise<CustomESG> {
    const data = await this.client.esg.data(ticker);
    return {
      score: data.score,
      grade: data.grade,
      environmental: data.environmental_score
    };
  }
}`}</Code>
          </div>
        </div>
      </div>


      {/* ---------- Repository Link ---------- */}
      <div className="mt-6 text-center">
        <a 
          href="https://github.com/axionquant/js-sdk" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          View Source on GitHub
        </a>
      </div>
    </div>
    </div>
  );
}
